/**
 * Detect data anomalies in the footnote placement algorithm (STEP FIVE).
 *
 * All DOM measurements must be pre-computed by the caller and passed in as
 * plain numbers/booleans.  This function is side-effect-free and returns a
 * plain flags/data object that the caller uses to decide which handlers to run.
 *
 * @param {{
 *   fnwIndex:                number,   - index into fnWLists for this wrapper (-1 = not found)
 *   index:                   number,   - position in editor.children
 *   boundaries:              Array,    - current page-break boundaries
 *   fnWLists:                Array,    - pre-built wrapper list from collectFootnoteData
 *   footnoteContentObject:   object,   - keyed by superscriptNumber
 *   superscriptsObject:      object,   - keyed by superscript text
 *   allFootnoteWrappers:     Array,    - raw [node, path] pairs from Slate
 *   deletedFootnoteWrappers: Array,    - mutable accumulator (read-only here)
 *   contentWithNoSource:     Array,    - fnwNode children with no matching superscript
 *   shallowOnChangeCount:    number,   - loop counter from component state
 *   isLastDocNode:           boolean,
 *   currentHeight:           number,   - accumulated height before this element (px)
 *   footNoteWrapperHeight:   number,   - total height of this footnote-wrapper (px)
 *   elBeforeMarginBottom:    number,   - margin-bottom of the element above (px)
 *   maxHeight:               number,   - current page max height (px)
 *   pageHeightInPxs:         number,   - ideal height of the current page (px)
 *   plainNextElementHeight:  number,   - getBoundingClientRect().height of next element (px)
 *   nextElementMarginBottom: number,   - margin-bottom of next element (px)
 *   defaultMarginInPx:       number,   - ptsToPxls(10) pre-computed by caller
 *   hasListAfterWrapper:     boolean,  - true when next node is a list with a rendered last item
 * }}
 * @returns {{
 *   maxIterationsReached:        boolean,
 *   tempContentToDelete:         Array,
 *   deletedObject:               object,
 *   sourceIsBeforeManual:        Array,
 *   isManual:                    boolean,
 *   sourceIsAtDifferentBoundary: Array,
 *   spaceBeforeList:             boolean,
 *   hasAnomalies:                boolean,
 * }}
 */
export function detectAnomalies({
	fnwIndex,
	index,
	boundaries,
	fnWLists,
	footnoteContentObject,
	superscriptsObject,
	allFootnoteWrappers,
	deletedFootnoteWrappers,
	contentWithNoSource,
	shallowOnChangeCount,
	isLastDocNode,
	currentHeight,
	footNoteWrapperHeight,
	elBeforeMarginBottom,
	maxHeight,
	pageHeightInPxs,
	plainNextElementHeight,
	nextElementMarginBottom,
	defaultMarginInPx,
	hasListAfterWrapper,
}) {
	// Convenience: content list for this wrapper (falsy when wrapper not found)
	const fnwContent = fnwIndex >= 0 && fnWLists[fnwIndex] && fnWLists[fnwIndex].content;

	// (1) Loop detection — shallowOnChangeCount accumulates each recursive onChange call.
	const maxIterationsReached = shallowOnChangeCount >= 6;

	// (2) Orphaned content: items whose superscript source no longer exists in the main body.
	//     Only populated when SOME (but not ALL) children are orphaned — all-orphaned means
	//     delete the whole wrapper (handled by anomaly 2).
	const tempContentToDelete = fnwContent
		&& contentWithNoSource.length > 0
		&& contentWithNoSource.length < fnwContent.length
		? contentWithNoSource.map(
			(c) => superscriptsObject[c] && superscriptsObject[c].contentPath && superscriptsObject[c].contentPath[1]
		)
		: [];

	// (3) Build an O(1) lookup of already-deleted wrapper positions.
	const deletedObject = deletedFootnoteWrappers.length > 0
		? deletedFootnoteWrappers.reduce((acc, curr) => {
			acc[curr.at] = curr;
			return acc;
		}, {})
		: {};

	// (4) Source superscript is in a page that ends with a manual break, but the
	//     footnote-wrapper is positioned after that break (wrong page).
	const sourceIsBeforeManual = fnwContent && boundaries.length > 2
		? fnwContent.filter((fnc) => {
			const sourcePath = superscriptsObject[fnc] && superscriptsObject[fnc].at[0];
			const secondToLast = boundaries[boundaries.length - 2];
			const targetPath = secondToLast && secondToLast.at && secondToLast.at[0];
			const isManualBoundary = secondToLast && secondToLast.manual;
			return isManualBoundary && sourcePath < targetPath;
		})
		: [];

	// (5) The boundary immediately preceding this wrapper is a manual page break.
	const isManual = !!(
		boundaries[boundaries.length - 1] && boundaries[boundaries.length - 1].manual
	);

	// (6) Source superscript lives on a different page from this footnote-wrapper.
	const sourceIsAtDifferentBoundary = fnwContent && boundaries.length > 0
		? fnwContent.map((fnc) => {
			const sourceAt = superscriptsObject[fnc] && superscriptsObject[fnc].at[0];
			const lastBoundary = boundaries[boundaries.length - 1];
			const secondToLast = boundaries.length > 1 ? boundaries[boundaries.length - 2] : null;
			const previousBoundaryAt = secondToLast && secondToLast.at && secondToLast.at[0];
			const footNoteNode = footnoteContentObject[fnc] ? footnoteContentObject[fnc].content : null;

			if (sourceAt === undefined || sourceAt === null) {
				return null;
			}

			if (sourceAt && previousBoundaryAt) {
				// More than one boundary: compare against the second-to-last
				const differentPage = sourceAt < previousBoundaryAt || (sourceAt === previousBoundaryAt && sourceAt > index) || sourceAt > index;
				//const differentPage = sourceAt <= previousBoundaryAt || sourceAt > index;
				const contentAt = footnoteContentObject[fnc] ? footnoteContentObject[fnc].at : null;
				return differentPage && contentAt
					? { sourceAt, superscriptNumber: fnc, contentAt, parent: allFootnoteWrappers[fnwIndex], footNoteNode }
					: null;

			} else if (sourceAt && !previousBoundaryAt) {
				// Only one boundary: check if superscript and wrapper are on the same page
				const boundaryIndex = lastBoundary && lastBoundary.at && lastBoundary.at[0]
					? lastBoundary.at[0]
					: null;
				const samePage = isLastDocNode
					? (currentHeight + footNoteWrapperHeight + defaultMarginInPx - elBeforeMarginBottom) < maxHeight
						&& boundaryIndex <= sourceAt
						&& index > sourceAt
					: boundaryIndex !== null
						? boundaryIndex < sourceAt && boundaryIndex > index
						: sourceAt < index;
				const contentAt = footnoteContentObject[fnc] ? footnoteContentObject[fnc].at : null;
				return samePage ? null : contentAt
					? { sourceAt, superscriptNumber: fnc, contentAt, parent: allFootnoteWrappers[fnwIndex], footNoteNode }
					: null;
			}

			return null;
		}).filter((fnc) => fnc !== null)
		: [];

	// (7) There is space after the wrapper on this page, but the next node is a list
	//     that would be partially cut off — the list needs to be split.
	const heightToCompare = pageHeightInPxs - currentHeight - footNoteWrapperHeight;
	const marginToAdd = nextElementMarginBottom > defaultMarginInPx
		? nextElementMarginBottom
		: defaultMarginInPx;
	const spaceBeforeList = hasListAfterWrapper
		&& heightToCompare > defaultMarginInPx
		&& heightToCompare < (plainNextElementHeight + marginToAdd);

	// Summary: any anomaly that blocks normal scenario processing.
	// Note: spaceBeforeList has its own separate handler and does NOT count here.
	const hasAnomalies = maxIterationsReached
		|| !!deletedObject[index]
		|| sourceIsBeforeManual.length > 0
		|| isManual
		|| sourceIsAtDifferentBoundary.length > 0;

	return {
		maxIterationsReached,
		tempContentToDelete,
		deletedObject,
		sourceIsBeforeManual,
		isManual,
		sourceIsAtDifferentBoundary,
		spaceBeforeList,
		hasAnomalies,
	};
}
